import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { 
  Upload, Download, Loader2, CheckCircle, AlertCircle,
  FileSpreadsheet, Tags, Sparkles, ArrowRight, Plus,
  AlertTriangle, Trash2, RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export const CategoryClassifierTab = ({ token }) => {
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(null);
  const [polling, setPolling] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [sensitiveCategories, setSensitiveCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newSensitiveCategoryName, setNewSensitiveCategoryName] = useState('');
  const [addingCategory, setAddingCategory] = useState(false);
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const fileInputRef = useRef(null);

  // Pending review queue (Cola de Dudosos)
  const [pendingItems, setPendingItems] = useState([]);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [pendingThreshold, setPendingThreshold] = useState(80);
  const [pendingEdits, setPendingEdits] = useState({}); // {pending_id: 'CAT, CAT'}
  const [pendingLoading, setPendingLoading] = useState(false);

  // GEO state
  const [geoFile, setGeoFile] = useState(null);
  const [geoFileName, setGeoFileName] = useState('');
  const [geoUploading, setGeoUploading] = useState(false);
  const [geoStatus, setGeoStatus] = useState(null);
  const [geoPolling, setGeoPolling] = useState(false);
  const [geoJobs, setGeoJobs] = useState([]);
  const [geoError, setGeoError] = useState('');
  const [geoOverwrite, setGeoOverwrite] = useState(false);
  const geoFileInputRef = useRef(null);

  useEffect(() => {
    if (token) {
      fetchCategories();
      fetchSensitiveCategories();
      fetchJobs();
    }
  }, [token]);

  useEffect(() => {
    let interval;
    if (polling) {
      interval = setInterval(fetchStatus, 2000);
    }
    return () => clearInterval(interval);
  }, [polling]);

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await axios.get(`${API}/admin/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchSensitiveCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/sensitive-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSensitiveCategories(response.data.categories || []);
    } catch (err) {
      console.error('Error fetching sensitive categories:', err);
    }
  };

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/category-classifier/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
      if (!response.data.in_progress && polling) {
        setPolling(false);
        fetchJobs();
        if (response.data.processed > 0) {
          toast.success(`Clasificación completada: ${response.data.processed} procesados`);
        }
      }
    } catch (err) {
      console.error('Error fetching status:', err);
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/admin/category-classifier/jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(response.data.jobs || []);
      setStatus(response.data.current_status || null);
    } catch (err) {
      console.error('Error fetching jobs:', err);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    toast.info('Las categorías se crean automáticamente al importar productos con esa categoría');
    setNewCategoryName('');
  };

  const handleAddSensitiveCategory = async () => {
    if (!newSensitiveCategoryName.trim()) return;
    setAddingCategory(true);
    try {
      await axios.post(`${API}/admin/sensitive-categories`, 
        { category: newSensitiveCategoryName.trim().toUpperCase() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewSensitiveCategoryName('');
      fetchSensitiveCategories();
      toast.success('Categoría sensible añadida');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al añadir categoría sensible');
    } finally {
      setAddingCategory(false);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFileName(selectedFile.name);
      setError('');
    }
  };

  const handleProcess = async () => {
    if (!file) {
      setError('Selecciona un archivo primero');
      return;
    }
    setUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(
        `${API}/admin/category-classifier/process?overwrite_existing=${overwriteExisting}`, formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(`Clasificación iniciada: ${response.data.total_rows} filas (${response.data.discarded_no_website} descartadas sin web)`);
      setPolling(true);
      setFile(null);
      setFileName('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al procesar');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (jobId, filename, filterMode = 'all') => {
    try {
      const response = await axios.get(
        `${API}/admin/category-classifier/download/${jobId}?filter_mode=${filterMode}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'categorizado.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Descarga completada');
    } catch (err) {
      toast.error('Error al descargar');
    }
  };

  const handleRemoveDead = async (jobId) => {
    if (!window.confirm('¿Eliminar permanentemente los dominios inactivos del Excel? No se podrán recuperar.')) return;
    try {
      const r = await axios.post(
        `${API}/admin/category-classifier/jobs/${jobId}/remove-dead`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${r.data.removed} inactivos eliminados`);
      fetchJobs();
    } catch (err) {
      toast.error('Error al eliminar inactivos');
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!confirm('¿Eliminar este resultado?')) return;
    try {
      await axios.delete(`${API}/admin/category-classifier/jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Eliminado');
      fetchJobs();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const clearFile = () => {
    setFile(null);
    setFileName('');
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ====== PENDING REVIEW QUEUE handlers ======
  const fetchPending = async () => {
    setPendingLoading(true);
    try {
      const r = await axios.get(`${API}/admin/category-classifier/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingItems(r.data.items || []);
      setPendingTotal(r.data.total || 0);
      setPendingThreshold(r.data.threshold || 80);
    } catch (err) {
      console.error('Error fetching pending:', err);
    } finally {
      setPendingLoading(false);
    }
  };

  const handleApprovePending = async (pendingId, edited) => {
    const cats = (edited ?? pendingEdits[pendingId] ?? '').trim();
    if (!cats) { toast.error('Escribe las categorías finales'); return; }
    try {
      await axios.post(
        `${API}/admin/category-classifier/pending/${pendingId}/approve`,
        { categories: cats },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Aprobado');
      fetchPending();
    } catch (err) {
      toast.error('Error al aprobar');
    }
  };

  const handleRejectPending = async (pendingId) => {
    if (!confirm('¿Descartar este dudoso?')) return;
    try {
      await axios.delete(`${API}/admin/category-classifier/pending/${pendingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Descartado');
      fetchPending();
    } catch (err) {
      toast.error('Error al descartar');
    }
  };

  const handleAcceptProposed = async (item) => {
    await handleApprovePending(item.pending_id, item.proposed_categories);
  };

  const handleDownloadPending = async () => {
    try {
      const r = await axios.get(
        `${API}/admin/category-classifier/pending/export`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `categorias_dudosos_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Descarga completada');
    } catch (err) {
      toast.error('No hay dudosos para descargar');
    }
  };

  useEffect(() => {
    if (token) fetchPending();
  }, [token]);

  // Re-fetch pending when classifier job finishes
  useEffect(() => {
    if (status && !status.in_progress && status.processed > 0) {
      fetchPending();
    }
  }, [status?.in_progress]);

  // ====== GEO handlers ======
  const fetchGeoStatus = async () => {
    try {
      const r = await axios.get(`${API}/admin/category-classifier/geo-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGeoStatus(r.data);
      if (!r.data.in_progress && geoPolling) {
        setGeoPolling(false);
        toast.success('Detección geo completada');
        fetchGeoJobs();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchGeoJobs = async () => {
    try {
      const r = await axios.get(`${API}/admin/category-classifier/detect-geo/jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGeoJobs(r.data.jobs || []);
      setGeoStatus(r.data.current_status || null);
    } catch (err) {
      console.error(err);
    }
  };

  const handleGeoFileSelect = (e) => {
    const f = e.target.files[0];
    if (f) {
      setGeoFile(f);
      setGeoFileName(f.name);
      setGeoError('');
    }
  };

  const handleGeoProcess = async () => {
    if (!geoFile) { setGeoError('Selecciona un archivo primero'); return; }
    setGeoUploading(true);
    setGeoError('');
    try {
      const formData = new FormData();
      formData.append('file', geoFile);
      const r = await axios.post(
        `${API}/admin/category-classifier/detect-geo/process?overwrite_existing=${geoOverwrite}`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(`Detección iniciada: ${r.data.total_rows} filas`);
      setGeoPolling(true);
      setGeoFile(null);
      setGeoFileName('');
      if (geoFileInputRef.current) geoFileInputRef.current.value = '';
    } catch (err) {
      setGeoError(err.response?.data?.detail || 'Error al procesar');
    } finally {
      setGeoUploading(false);
    }
  };

  const handleGeoDownload = async (jobId, filename) => {
    try {
      const r = await axios.get(
        `${API}/admin/category-classifier/detect-geo/download/${jobId}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', filename || 'geo.xlsx');
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Descarga completada');
    } catch (err) {
      toast.error('Error al descargar');
    }
  };

  const handleGeoDeleteJob = async (jobId) => {
    if (!confirm('¿Eliminar este resultado?')) return;
    try {
      await axios.delete(`${API}/admin/category-classifier/detect-geo/jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Eliminado');
      fetchGeoJobs();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const clearGeoFile = () => {
    setGeoFile(null);
    setGeoFileName('');
    setGeoError('');
    if (geoFileInputRef.current) geoFileInputRef.current.value = '';
  };

  // Poll geo status while running
  useEffect(() => {
    let interval;
    if (geoPolling) {
      interval = setInterval(fetchGeoStatus, 2000);
    }
    return () => clearInterval(interval);
  }, [geoPolling]);

  // Initial geo fetch
  useEffect(() => {
    if (token) fetchGeoJobs();
  }, [token]);

  const geoProgressPercent = geoStatus?.total > 0
    ? Math.round((geoStatus.processed / geoStatus.total) * 100)
    : 0;

  const progressPercent = status?.total > 0
    ? Math.round((status.processed / status.total) * 100)
    : 0;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Categories Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tags className="h-5 w-5 text-purple-600" />
            Categorías Disponibles ({categories.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input placeholder="Nueva categoría..." value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value.toUpperCase())} className="max-w-xs"
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddCategory(); }} />
            <Button onClick={handleAddCategory} variant="outline" size="sm">
              <Plus className="h-4 w-4 mr-1" /> Añadir
            </Button>
          </div>
          {loadingCategories ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando...
            </div>
          ) : categories.length > 0 ? (
            <div className="flex flex-wrap gap-1 max-h-48 overflow-y-auto p-2 bg-slate-50 rounded-lg">
              {categories.map((cat, idx) => (
                <Badge key={idx} variant="secondary" className="text-xs">{cat}</Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500">No hay categorías</p>
          )}
        </CardContent>
      </Card>

      {/* Sensitive Categories */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Categorías Sensibles ({sensitiveCategories.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input placeholder="Nueva categoría sensible..." value={newSensitiveCategoryName}
              onChange={(e) => setNewSensitiveCategoryName(e.target.value.toUpperCase())} className="max-w-xs"
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddSensitiveCategory(); }} />
            <Button onClick={handleAddSensitiveCategory} variant="outline" size="sm" disabled={addingCategory}
              className="border-amber-500 text-amber-600 hover:bg-amber-50">
              {addingCategory ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
              Añadir
            </Button>
          </div>
          {sensitiveCategories.length > 0 ? (
            <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto p-2 bg-amber-50 rounded-lg">
              {sensitiveCategories.map((cat, idx) => (
                <Badge key={idx} variant="destructive" className="text-xs bg-amber-100 text-amber-800">{cat}</Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500">No hay categorías sensibles</p>
          )}
        </CardContent>
      </Card>

      {/* Classifier */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-violet-600" />
            Clasificador de Categorías (IA)
          </CardTitle>
          <CardDescription>
            Sube Excel con dominios web. La IA asigna MÚLTIPLES categorías afines separadas por comas. Filas sin website se descartan.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Upload */}
          <div className="space-y-4">
            <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept=".xlsx,.xls,.csv" className="hidden" />
            {!fileName ? (
              <div onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-purple-400 hover:bg-purple-50/50 transition-colors">
                <FileSpreadsheet className="h-12 w-12 mx-auto text-slate-400 mb-3" />
                <p className="text-slate-600 font-medium">Haz clic para seleccionar un archivo</p>
                <p className="text-sm text-slate-400 mt-1">.xlsx, .xls o .csv</p>
              </div>
            ) : (
              <div className="flex items-center justify-between bg-slate-100 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="h-8 w-8 text-green-600" />
                  <p className="font-medium text-slate-700">{fileName}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={clearFile}>Cambiar</Button>
              </div>
            )}
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Overwrite toggle */}
          <label className="flex items-start gap-3 p-3 rounded-lg border bg-amber-50/50 cursor-pointer hover:bg-amber-50">
            <input
              type="checkbox"
              checked={overwriteExisting}
              onChange={(e) => setOverwriteExisting(e.target.checked)}
              className="mt-1 h-4 w-4 accent-amber-600"
              data-testid="overwrite-existing-checkbox"
            />
            <div className="text-sm">
              <p className="font-medium text-amber-800">Reclasificar todas las filas (sobrescribir)</p>
              <p className="text-xs text-amber-700/80 mt-0.5">
                Por defecto se conservan las categorías ya presentes si son válidas. Activa esto para forzar a la IA a reclasificar todo (útil cuando hay datos sucios como "NETHERLANDS" o categorías inventadas).
              </p>
            </div>
          </label>

          <Button onClick={handleProcess}
            disabled={!file || uploading || status?.in_progress}
            className="w-full bg-purple-600 hover:bg-purple-700"
            data-testid="classify-btn">
            {uploading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Subiendo...</>
            ) : (
              <><Sparkles className="h-4 w-4 mr-2" /> Clasificar Categorías</>
            )}
          </Button>

          {/* Progress */}
          {status?.in_progress && (
            <div className="space-y-3 p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-purple-700 flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Clasificando...
                </span>
                <span className="text-sm text-purple-600">{progressPercent}%</span>
              </div>
              <Progress value={progressPercent} className="h-2" />
              <div className="flex justify-between text-xs text-purple-600">
                <span>Procesados: {status.processed}</span>
                <span>Errores: {status.errors}</span>
                <span>Total: {status.total}</span>
              </div>
            </div>
          )}

          {/* Refresh */}
          <Button variant="outline" size="sm" onClick={() => { fetchStatus(); fetchJobs(); fetchPending(); }}>
            <RefreshCw className="h-4 w-4 mr-2" /> Actualizar estado
          </Button>
        </CardContent>
      </Card>

      {/* Pending Review Queue (Cola de Dudosos) */}
      <Card className="border-amber-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            Cola de Dudosos
            {pendingTotal > 0 && (
              <Badge variant="destructive" className="ml-2">{pendingTotal}</Badge>
            )}
          </CardTitle>
          <CardDescription>
            Dominios clasificados con baja confianza (&lt; {pendingThreshold}). Revisa, edita o acepta la propuesta de la IA.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={fetchPending} disabled={pendingLoading}>
              {pendingLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Refrescar
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              onClick={handleDownloadPending}
              disabled={pendingTotal === 0}
              data-testid="download-pending-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Descargar dudosos
            </Button>
          </div>

          {pendingItems.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
              No hay dudosos. Todas las clasificaciones tienen confianza ≥ {pendingThreshold}.
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto" style={{ maxHeight: '500px' }}>
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left">Dominio</th>
                      <th className="px-3 py-2 text-left">Propuesta IA</th>
                      <th className="px-3 py-2 text-center">Conf.</th>
                      <th className="px-3 py-2 text-left">Razón</th>
                      <th className="px-3 py-2 text-left">Final (editar)</th>
                      <th className="px-3 py-2 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingItems.map((it) => (
                      <tr key={it.pending_id} className="border-t">
                        <td className="px-3 py-2 font-mono text-xs">
                          <a href={`https://${it.website}`} target="_blank" rel="noopener noreferrer"
                             className="text-blue-600 hover:underline">{it.website}</a>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {(it.proposed_categories || '').split(',').map((c, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">{c.trim()}</Badge>
                            ))}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <Badge className={
                            it.confidence >= 60 ? 'bg-yellow-100 text-yellow-800 border-yellow-200' :
                            it.confidence >= 40 ? 'bg-orange-100 text-orange-800 border-orange-200' :
                            'bg-red-100 text-red-800 border-red-200'
                          }>{it.confidence}</Badge>
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground max-w-xs truncate"
                            title={it.reasoning}>{it.reasoning || '-'}</td>
                        <td className="px-3 py-2">
                          <Input
                            value={pendingEdits[it.pending_id] ?? it.proposed_categories}
                            onChange={(e) => setPendingEdits({
                              ...pendingEdits, [it.pending_id]: e.target.value.toUpperCase()
                            })}
                            placeholder="CAT1, CAT2"
                            className="h-8 text-xs"
                          />
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <Button
                            size="sm"
                            variant="default"
                            className="h-7 bg-green-600 hover:bg-green-700 mr-1"
                            onClick={() => handleAcceptProposed(it)}
                            title="Aceptar la propuesta de la IA tal cual"
                          >
                            <CheckCircle className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 mr-1"
                            onClick={() => handleApprovePending(it.pending_id)}
                            title="Aprobar con tu edición"
                          >
                            Guardar
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            className="h-7"
                            onClick={() => handleRejectPending(it.pending_id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* GEO Detection (País + Idioma) */}
      <Card className="border-blue-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-blue-600" />
            Detectar País e Idioma
          </CardTitle>
          <CardDescription>
            Sube Excel con dominios web. Detecta automáticamente <strong>país</strong> e <strong>idioma</strong> usando TLD (.nl, .fr, .es…), el atributo <code>&lt;html lang&gt;</code> del sitio, y la IA como respaldo.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <input type="file" ref={geoFileInputRef} onChange={handleGeoFileSelect} accept=".xlsx,.xls,.csv" className="hidden" />
          {!geoFileName ? (
            <div onClick={() => geoFileInputRef.current?.click()}
              className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors">
              <FileSpreadsheet className="h-12 w-12 mx-auto text-slate-400 mb-3" />
              <p className="text-slate-600 font-medium">Haz clic para seleccionar un archivo</p>
              <p className="text-sm text-slate-400 mt-1">.xlsx, .xls o .csv</p>
            </div>
          ) : (
            <div className="flex items-center justify-between bg-slate-100 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <FileSpreadsheet className="h-8 w-8 text-blue-600" />
                <p className="font-medium text-slate-700">{geoFileName}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={clearGeoFile}>Cambiar</Button>
            </div>
          )}

          {geoError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{geoError}</AlertDescription>
            </Alert>
          )}

          <label className="flex items-start gap-3 p-3 rounded-lg border bg-amber-50/50 cursor-pointer hover:bg-amber-50">
            <input
              type="checkbox"
              checked={geoOverwrite}
              onChange={(e) => setGeoOverwrite(e.target.checked)}
              className="mt-1 h-4 w-4 accent-amber-600"
              data-testid="geo-overwrite-checkbox"
            />
            <div className="text-sm">
              <p className="font-medium text-amber-800">Sobrescribir país/idioma existentes</p>
              <p className="text-xs text-amber-700/80 mt-0.5">
                Por defecto se conservan los valores ya presentes. Actívalo para recalcular todo (útil si hay datos sucios).
              </p>
            </div>
          </label>

          <Button onClick={handleGeoProcess}
            disabled={!geoFile || geoUploading || geoStatus?.in_progress}
            className="w-full bg-blue-600 hover:bg-blue-700"
            data-testid="geo-process-btn">
            {geoUploading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Subiendo...</>
            ) : (
              <><Sparkles className="h-4 w-4 mr-2" /> Detectar País e Idioma</>
            )}
          </Button>

          {geoStatus?.in_progress && (
            <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-blue-700 flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Detectando...
                </span>
                <span className="text-sm text-blue-600">{geoProgressPercent}%</span>
              </div>
              <Progress value={geoProgressPercent} className="h-2" />
              <div className="flex justify-between text-xs text-blue-600 flex-wrap gap-2">
                <span>Procesados: {geoStatus.processed}</span>
                <span>Scrapeados: {geoStatus.scraped}</span>
                <span>IA: {geoStatus.ai_calls}</span>
                <span>Solo TLD: {geoStatus.tld_only}</span>
                <span>Errores: {geoStatus.errors}</span>
                <span>Total: {geoStatus.total}</span>
              </div>
            </div>
          )}

          {/* Geo jobs history */}
          {geoJobs.length > 0 && (
            <div className="space-y-2 pt-2">
              <h4 className="text-sm font-medium text-slate-700">Resultados anteriores</h4>
              {geoJobs.map((job) => {
                const filename = job.original_filename || 'geo.xlsx';
                const baseName = filename.includes('.') ? filename.split('.').slice(0, -1).join('.') : filename;
                return (
                  <div key={job.job_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{filename}</p>
                      <div className="flex gap-3 text-xs text-muted-foreground mt-1 flex-wrap">
                        <span>{formatDate(job.created_at)}</span>
                        {job.stats && (
                          <>
                            <span className="text-blue-600">{job.stats.processed} procesados</span>
                            <span className="text-green-600">{job.stats.scraped} scrap</span>
                            <span className="text-purple-600">{job.stats.ai_calls} IA</span>
                            <span className="text-red-600">{job.stats.errors} errores</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 ml-3 shrink-0">
                      <Button size="sm" variant="default" className="bg-blue-600 hover:bg-blue-700"
                        onClick={() => handleGeoDownload(job.job_id, `${baseName}_geo.xlsx`)}
                        data-testid={`geo-download-${job.job_id}`}>
                        <Download className="h-4 w-4 mr-1" /> Descargar
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => handleGeoDeleteJob(job.job_id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Jobs History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-green-600" />
            Resultados de Clasificaciones
          </CardTitle>
        </CardHeader>
        <CardContent>
          {jobs.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No hay clasificaciones guardadas</p>
          ) : (
            <div className="space-y-3">
              {jobs.map((job) => {
                const filename = job.original_filename || 'clasificado.xlsx';
                const baseName = filename.includes('.') ? filename.split('.').slice(0, -1).join('.') : filename;
                const deadCount = job.stats?.dead_count || 0;
                return (
                  <div key={job.job_id} className="p-3 bg-slate-50 rounded-lg border space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{filename}</p>
                        <div className="flex gap-3 text-xs text-muted-foreground mt-1 flex-wrap">
                          <span>{formatDate(job.created_at)}</span>
                          {job.stats && (
                            <>
                              <span className="text-green-600">{job.stats.processed} procesados</span>
                              {job.stats.errors > 0 && <span className="text-red-600">{job.stats.errors} errores</span>}
                              {deadCount > 0 && (
                                <Badge variant="destructive" className="text-xs">
                                  {deadCount} dominios inactivos
                                </Badge>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      <Button size="sm" variant="destructive" onClick={() => handleDeleteJob(job.job_id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button size="sm" variant="default" className="bg-green-600 hover:bg-green-700"
                        onClick={() => handleDownload(job.job_id, `${baseName}_categorizado.xlsx`, 'all')}>
                        <Download className="h-4 w-4 mr-1" /> Descargar
                      </Button>
                      {deadCount > 0 && (
                        <>
                          <Button size="sm" variant="outline" className="border-green-300 text-green-700 hover:bg-green-50"
                            onClick={() => handleDownload(job.job_id, `${baseName}_sin_inactivos.xlsx`, 'exclude_dead')}
                            data-testid={`download-no-dead-${job.job_id}`}>
                            <Download className="h-4 w-4 mr-1" /> Sin inactivos
                          </Button>
                          <Button size="sm" variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50"
                            onClick={() => handleDownload(job.job_id, `${baseName}_solo_inactivos.xlsx`, 'only_dead')}
                            data-testid={`download-only-dead-${job.job_id}`}>
                            <Download className="h-4 w-4 mr-1" /> Solo inactivos
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleRemoveDead(job.job_id)}
                            data-testid={`remove-dead-${job.job_id}`}>
                            <Trash2 className="h-4 w-4 mr-1" /> Eliminar inactivos
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CategoryClassifierTab;
